import {
  BessSubmercado,
  FormValues,
  PerfilConsumo,
  SimulationPayloadItem,
} from "./types";
import { parseBrazilianNumber } from "../../utils/utilityFunctions";
import { filterMeasurementCsvByPoint } from "./bess/bessCsv";
import { CAMPOS_OMITIDOS_DO_PAYLOAD } from "./situacaoUsina";

export const parseNum = (value: string) =>
  String(value).trim() === "" ? 0 : parseBrazilianNumber(value);

export function transformValues(values: FormValues): SimulationPayloadItem {
  const isGerador =
    values.isGerador &&
    !["MLXAPE", "MLBESS", "APEBESS"].includes(values.simulationType);
  const possuiUsina = !["ML", "MLBESS"].includes(values.simulationType);
  // situacaoUsina só existe onde o formulário de autoprodução aparece. Em
  // GD → APE o backend infere o modo MIGRACAO_GD_APE pelo par de tipos.
  const usaSituacaoUsina =
    values.simulationType === "APE" ||
    values.simulationType === "MLXAPE" ||
    values.simulationType === "APEBESS";

  const payload: SimulationPayloadItem = {
    demandaForaPonta: parseNum(values.demandaForaPonta),
    demandaPonta: parseNum(values.demandaPonta),
    demandaForaPontaUltrapassagem: parseNum(
      values.demandaForaPontaUltrapassagem
    ),
    demandaPontaUltrapassagem: parseNum(values.demandaPontaUltrapassagem),
    consumoPonta: parseNum(values.consumoPonta),
    consumoForaPonta: parseNum(values.consumoForaPonta),
    custoGestao: values.custoGestao ? parseNum(values.custoGestao) : -1,
    isGerador,
    consumoGerador: isGerador ? parseNum(values.consumoGerador) : 0,
    custoDiesel: isGerador ? parseNum(values.custoDiesel) : 0,
    custoGerador: isGerador ? parseNum(values.custoGerador) : 0,
    isBateria: values.isBateria,
    isDescontoGarantido: values.isDescontoGarantido,
    potenciaBateria: values.isBateria ? parseNum(values.potenciaBateria) : 0,
    energiaMaximaArmazenada: values.isBateria
      ? parseNum(values.energiaMaximaArmazenada)
      : 0,
    investimentoBateria: values.isBateria
      ? parseNum(values.investimentoBateria)
      : 0,
    rteBateria: values.isBateria
      ? +(parseNum(values.rteBateria) / 100).toFixed(2)
      : 0,
    distribuidora: values.distribuidora,
    ths: values.ths,
    grupoTarifario: values.grupoTarifario,
    pis: parseNum(values.pis) / 100,
    cofins: parseNum(values.cofins) / 100,
    icms: parseNum(values.icms) / 100,
    custoProjeto: usaSituacaoUsina ? parseNum(values.custoProjeto) : 0,
    taxaAtratividade: parseNum(values.taxaAtratividade),
    reajusteDistribuidora: parseNum(values.reajusteDistribuidora),
    reajusteEnergia: parseNum(values.reajusteEnergia),
    initialYear: +values.initialYear,
    dataSimulacao: new Date().toISOString(),
    descontoTusdApe: values.descontoTusdApe,
    precoEnergia: values.precoEnergia.map((value) => parseNum(value)),
    desconto: values.desconto,
    isTaxCovid: values.isTaxCovid,
    isTaxScarcity: values.isTaxScarcity,
    isEncargos: values.isEncargos,
    precoEnergiaGeracao: values.precoEnergiaGeracao.map((value) =>
      parseNum(value)
    ),
    simultaneidade: possuiUsina
      ? parseFloat(String(values.simultaneidade)) / 100
      : 0,
    demandaUsina: possuiUsina ? parseNum(values.demandaUsina) : 0,
    geracao: possuiUsina ? parseNum(values.geracao) : 0,
    local: values.local,
    abateICMS: values.abateICMS,
  };

  if (usaSituacaoUsina) {
    payload.situacaoUsina = values.situacaoUsina;

    CAMPOS_OMITIDOS_DO_PAYLOAD[values.situacaoUsina].forEach((campo) => {
      delete payload[campo];
    });

    if (values.situacaoUsina === "USINA_CONSORCIO") {
      // O consórcio não injeta: o BESS opera apenas sobre consumo.
      payload.local = false;
    }
  }

  if (values.simulationType === "GDXAPE") {
    // GD → APE: a API exige o campo, mas o valor é sempre zero.
    payload.simultaneidade = 0;
  }

  if (values.isBateria) {
    payload.submercado = values.submercado as BessSubmercado;
    payload.dod = +(parseNum(values.dod) / 100).toFixed(2);

    if (values.medicaoCsv) {
      payload.medicaoCsv = values.medicaoCsv;
      if (values.pontoMedicaoBess) {
        payload.pontoMedicaoBess = values.pontoMedicaoBess;
      }
    } else {
      // Modo sintético: o mês seleciona calendário e PLD; dataSimulacao segue
      // selecionando as tarifas.
      payload.mesReferencia = values.mesReferencia;
      payload.perfilConsumo = values.perfilConsumo as PerfilConsumo;
    }
  }

  return payload;
}

/**
 * Reduz o CSV do payload aos registros do ponto de medição selecionado, para
 * não trafegar as demais unidades do arquivo. Devolve um payload novo — o CSV
 * completo continua no estado do formulário, então trocar de ponto refiltra a
 * partir do original.
 *
 * Retorna null quando o ponto selecionado não aparece no CSV; nesse caso a
 * comparação não deve ser enviada.
 */
export function withFilteredMedicaoCsv(
  payload: SimulationPayloadItem,
  pontoMedicao: string
): SimulationPayloadItem | null {
  if (!payload.medicaoCsv || !pontoMedicao.trim()) return payload;

  const medicaoCsv = filterMeasurementCsvByPoint(
    payload.medicaoCsv,
    pontoMedicao
  );
  if (medicaoCsv === null) return null;

  return { ...payload, medicaoCsv };
}
